import type { ColDef, RowClickedEvent, ValueFormatterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { DateTime } from 'luxon';
import { type FC, useContext, useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { CheckCircleFill, ExclamationTriangleFill } from 'react-bootstrap-icons';
import { useForm } from 'react-hook-form';
import type {
  MewsReservation,
  MewsReservationSyncResponse,
  Reservations,
} from '../../../modules/interfaces';
import './styles/index.css';

import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import { useAuth, useMutationRequest, useNotifications } from '../../../modules/hooks';
import { endpoints } from '../../../modules/mappers/urls';
import { roomSort } from '../../../modules/utils/tableSorts';
import { getApiErrorMessage } from '../../../modules/utils/transform';
import { DataTable } from '../../Tables/DataTable';
import { BasicModal } from '../Basic';

interface PropertyConfigModalProps {
  selectedReservations?: MewsReservation[];
  show: boolean;
  handleClose: () => void;
  refreshHandler?: () => void;
  fromDetails?: boolean;
}

interface ReservationIssue {
  email: string;
  name: string;
  phone: string;
  reservationId: string;
  date: string;
  reason: string;
  message: string;
}

export const paymentStatusColorMapper: Record<string, string> = {
  CONFIRMED: 'primary',
  STARTED: 'warning',
};

export const SynchronizeModal: FC<PropertyConfigModalProps> = ({
  selectedReservations,
  show,
  handleClose,
  refreshHandler,
  fromDetails = false,
}) => {
  const [searchString] = useState<string>('');
  const [successModal, setSuccessModal] = useState(false);
  const { setSimpleToasts } = useNotifications()!;
  const { selectedProperty } = useContext(UserPropertiesContext)!;
  const [rowData, setRowData] = useState<ReservationIssue[]>([]);
  const [selectedReservationsRows, setSelectedReservationsRows] = useState<
    MewsReservation[]
  >([]);

  const { credentialsInfo } = useAuth()!;

  const { register, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: {
      reservationsIds: [],
      sendLastMinuteMessages: false,
      forceReservationCreation: true,
      onlyCreateConversation: false,
    },
  });

  const handleSuccess = () => {
    setSuccessModal(false);
    reset({
      reservationsIds: [],
      sendLastMinuteMessages: false,
      forceReservationCreation: true,
      onlyCreateConversation: false,
    });
    if (refreshHandler) {
      refreshHandler();
    }
    handleClose();
  };

  const [
    { data: syncResult, loading: loadingSync, error: errorSync },
    syncMewsReservations,
  ] = useMutationRequest<MewsReservationSyncResponse>(
    endpoints.SYNC_RESERVATION,
    'post',
    {
      authToken: credentialsInfo?.token,
      propertyId: selectedProperty?.property.uuid,
    },
    { manual: true },
  );

  const sendLastMinuteMessages = watch('sendLastMinuteMessages');
  const forceReservationCreation = watch('forceReservationCreation');
  const onlyCreateConversation = watch('onlyCreateConversation');

  const onSendLastMinuteMessagesChange = (editorState: boolean) => {
    setValue('sendLastMinuteMessages', editorState);
  };

  const onForceReservationCreationChange = (editorState: boolean) => {
    setValue('forceReservationCreation', editorState);
  };
  const onOnlyCreateConversationChange = (editorState: boolean) => {
    setValue('onlyCreateConversation', editorState);
  };

  const onSubmit = () => {
    if (selectedReservationsRows?.length) {
      const serviceId = selectedReservationsRows[0].ServiceId;
      const reservationIds = selectedReservationsRows?.map((value) => value.Id);
      syncMewsReservations({
        reservationIds,
        sendLastMinuteMessages,
        forceReservationCreation,
        onlyCreateConversation,
        propertyId: selectedProperty?.property.uuid,
        serviceId,
      });
    }
  };

  const copyToClipboardHandler = (data: ReservationIssue) => {
    navigator.clipboard.writeText(data.message);
    setSimpleToasts({
      type: 'success',
      message: 'Error reason copied successfully',
      show: true,
    });
  };

  const selectedRows = (data: MewsReservation[]) => {
    setSelectedReservationsRows(data);
  };

  const [columns] = useState<ColDef<MewsReservation>[]>([
    {
      headerName: 'ROOM',
      flex: 1,
      sortable: true,
      comparator: (valueA, valueB) => roomSort(valueA, valueB),
      valueGetter: (params) => {
        const r = params.data?.Resource;
        return r ? `${r.Name} - ${r.ResourceName}` : 'Not assigned';
      },
      filterValueGetter: (params) => {
        const r = params.data?.Resource;
        return r ? `${r.Name} - ${r.ResourceName}` : 'Not assigned';
      },
    },
    {
      headerName: 'GUEST NAME',
      flex: 1,
      sortable: true,
      valueGetter: (params) => {
        const c = params.data?.Customer;
        return c ? `${c.FirstName} ${c.LastName}` : '-';
      },
      filterValueGetter: (params) => {
        const c = params.data?.Customer;
        return c ? `${c.FirstName} ${c.LastName}` : '-';
      },
    },
    {
      field: 'ScheduledStartUtc',
      headerName: 'ARRIVAL DATE',
      sortable: true,
      flex: 1,
      valueFormatter: (p) => DateTime.fromISO(p.value || '').toFormat('dd LLL yyyy'),
      filterValueGetter: (p) => DateTime.fromISO(p.data?.ScheduledStartUtc || '').toISO(),
    },
    {
      field: 'EndUtc',
      headerName: 'DEPARTURE DATE',
      sortable: true,
      flex: 1,
      valueFormatter: (p) => DateTime.fromISO(p.value || '').toFormat('dd LLL yyyy'),
      filterValueGetter: (p) => DateTime.fromISO(p.data?.EndUtc || '').toISO(),
    },
  ]);

  const errorColumnDefs: ColDef<ReservationIssue>[] = [
    { headerName: 'Email', field: 'email', flex: 1 },
    { headerName: 'Name', field: 'name', flex: 1 },
    { headerName: 'Phone', field: 'phone', flex: 1 },
    { headerName: 'Reservation ID', field: 'reservationId', flex: 1 },

    { headerName: 'Reason', field: 'reason', flex: 2 },
  ];
  useEffect(() => {
    register('sendLastMinuteMessages');
    register('forceReservationCreation');
    register('onlyCreateConversation');
  }, [register]);

  useEffect(() => {
    if (syncResult) {
      if (syncResult?.result?.syncResults?.errorReasons?.length >= 1) {
        const parsed = syncResult?.result?.syncResults?.errorReasons
          ?.map((msg) => {
            const match = msg.match(
              /"([^"]+)" - "([^"]+)" with phone number "([^"]+)" and ReservationId "([^"]+)" starting on "([^"]+)".+Reason: "([^"]+)"/,
            );

            if (match) {
              return {
                email: match[1],
                name: match[2],
                phone: match[3],
                reservationId: match[4],
                date: match[5],
                reason: match[6],
                message: msg,
              };
            }

            return null;
          })
          .filter((item): item is ReservationIssue => item !== null);
        setRowData(parsed);
      }

      setSuccessModal(true);
    }
    if (errorSync) {
      const message = getApiErrorMessage(errorSync);
      setSimpleToasts({ type: 'danger', message, show: true });
    }
  }, [errorSync, syncResult]);

  useEffect(() => {
    if (fromDetails && selectedReservations?.length) {
      setSelectedReservationsRows(selectedReservations as MewsReservation[]);
    }
  }, [fromDetails]);

  return (
    <>
      <Modal
        show={show}
        onHide={() => {
          handleClose();
        }}
        size="xl"
        backdrop="static"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Header>
            <Modal.Title className="h5">Sync Reservations</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="mt-16px">
              <Col xs={3}>
                <Form.Check
                  type="checkbox"
                  defaultChecked={sendLastMinuteMessages}
                  onChange={(e) =>
                    onSendLastMinuteMessagesChange(e.currentTarget.checked)
                  }
                  label="Send last minute messages"
                />
              </Col>
              <Col xs={3}>
                <Form.Check
                  type="checkbox"
                  defaultChecked={forceReservationCreation}
                  onChange={(e) =>
                    onForceReservationCreationChange(e.currentTarget.checked)
                  }
                  label="Force creation"
                />
              </Col>
              <Col xs={3}>
                <Form.Check
                  type="checkbox"
                  defaultChecked={onlyCreateConversation}
                  onChange={(e) =>
                    onOnlyCreateConversationChange(e.currentTarget.checked)
                  }
                  label="Only create conversation"
                />
              </Col>
            </Row>

            {selectedReservations && selectedReservations.length > 0 ? (
              <DataTable
                changeState={0}
                rowData={selectedReservations}
                columnDefs={columns}
                showSearchInput={!fromDetails}
                pagination
                paginationPageSize={5}
                rowSelect={!fromDetails ? true : false}
                onSelection={!fromDetails ? selectedRows : undefined}
              />
            ) : (
              <div className="no-reservation-note-text">No Add-on was selected</div>
            )}
          </Modal.Body>
          <Modal.Footer style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div>
              <Button
                style={{ marginRight: '10px' }}
                className="cancel-button"
                onClick={handleClose}
                disabled={loadingSync}
              >
                Cancel
              </Button>
              <Button
                className="edit-guest-modal-button-save"
                type="submit"
                disabled={loadingSync || !selectedReservationsRows?.length}
              >
                {loadingSync ? 'Loading...' : 'Sync'}
              </Button>
            </div>
          </Modal.Footer>
        </form>
      </Modal>

      {syncResult && (
        <BasicModal
          show={successModal}
          handleClose={() => {
            handleSuccess();
          }}
          title=""
          size={syncResult?.result?.syncResults?.errorCount === 0 ? 'sm' : 'xl'}
        >
          <Row>
            <Col style={{ display: 'flex', justifyContent: 'center' }}>
              {syncResult?.result?.syncResults?.errorCount === 0 ? (
                <CheckCircleFill size={80} color="#2ce983" />
              ) : (
                <ExclamationTriangleFill size={80} color="#f4eeb9" />
              )}
            </Col>
          </Row>

          {syncResult?.result?.syncResults?.errorCount === 0 &&
            syncResult?.result?.syncResults?.successCount >= 1 && (
              <>
                <Row>
                  <Col className="sync-modal-columns" style={{ fontSize: '20px' }}>
                    Success
                  </Col>
                </Row>
                <Row>
                  <Col className="sync-modal-columns">
                    Success count: {syncResult?.result?.syncResults?.successCount || 0}
                  </Col>
                </Row>
              </>
            )}

          {syncResult?.result?.syncResults?.errorCount >= 1 && (
            <>
              <Row>
                <Col className="sync-modal-columns" style={{ fontSize: '20px' }}>
                  Completed with errors
                </Col>
              </Row>
              <Row>
                <Col sm={12} className="sync-modal-columns">
                  Success count: {syncResult?.result?.syncResults?.successCount || 0}
                  <br />
                  Error count: {syncResult?.result?.syncResults?.errorCount || 0}
                  <br />
                </Col>
                <Col
                  sm={12}
                  style={{
                    marginLeft: '10px',
                    marginTop: '20px',
                    fontSize: '14px',
                  }}
                >
                  The following reservations failed to sync:
                </Col>

                <Col className="sync-modal-columns" style={{ marginTop: '0px' }}>
                  <DataTable
                    changeState={0}
                    rowData={rowData}
                    columnDefs={errorColumnDefs}
                    showSearchInput={false}
                    pagination
                    paginationPageSize={5}
                    onRowClicked={(e: RowClickedEvent<ReservationIssue>) =>
                      copyToClipboardHandler(e.data!)
                    }
                  />
                </Col>
              </Row>
            </>
          )}
        </BasicModal>
      )}
    </>
  );
};
